/**
 * L'admission d'un travail de rédaction dans le résident EXACT de sa
 * conversation, avant tout enqueue (#1202, réordonnée par #1337).
 *
 * Consentement de l'avocat et capacité SuperDoc sont deux contrats différents :
 * le backend n'enfile le job qu'après ce reçu, qui garantit un éditeur prêt,
 * épinglé, et — pour une génération — le plan tel qu'il est à l'écran.
 *
 * ## L'ordre est le contrat (correction de D4, #1337)
 *
 * `requestResident` → `ready` → épingle → capture de la révision et du plan sur
 * le montage SOURCE → si le travail remplace le document, `replaceDocument` en
 * PRÉSENTANT une épingle unique → attente du montage remplaçant, sur son époque
 * exacte → reçu portant la NOUVELLE époque.
 *
 *  - **Capturer avant de remplacer** : le plan vit dans l'éditeur physique du
 *    source, que le remplacement détruit. L'ordre inverse n'était possible que
 *    parce que l'ancienne préparation (`capturePresentation`) ne touchait pas
 *    l'éditeur — c'est-à-dire parce que présentation et montage divergeaient,
 *    le défaut même que D4 ferme.
 *  - **Remplacer avant le reçu** : `redaction_job_started` confronte l'époque
 *    de l'écho au montage physique (`memeMontageEpoqueExigee`). Un reçu portant
 *    l'époque source serait refusé dès que le document a été remplacé ; et
 *    remplacer APRÈS la liaison obligerait `redaction_job_started` à remonter,
 *    ce que le ticket interdit (événement rejouable, user story 26).
 *  - **Une épingle par tour et par document** : `tryBeginExclusiveReplacement`
 *    exige `pinCount === 1`. Si le tour tient déjà une épingle sur ce document,
 *    l'admission la RÉUTILISE ; en poser une seconde ferait refuser le bail au
 *    demandeur lui-même (mesuré par le lot P : `pinCount = 2`, bail refusé).
 *  - **Présenter, jamais rendre** : rendre l'épingle du tour pour obtenir le bail
 *    annulerait, depuis D9, le chargement même qui le demande (`8bcd40f7c`).
 *
 * ## Un travail qui porte un papier à en-tête (`depart`)
 *
 * - **Une recherche (plan, ou acte en un seul temps) démarre TOUJOURS un document
 *   neuf** sur son papier à en-tête, quel que soit le document ouvert : Word importé,
 *   modèle de bibliothèque, brouillon d'un autre document. Le travail écrivait
 *   sinon à la suite de ce document. Le geste est celui de « Nouveau »
 *   (`ouvrirUnDocumentVierge`) : le sortant est enregistré et reste dans
 *   « Documents de cette conversation », la page neuve est détachée de son
 *   identité, l'épingle du travail est présentée au bail.
 * - **Une génération hérite du modèle du plan** : elle remplace le plan comme
 *   avant, et le document qu'elle pose part de ce modèle — aussi après un
 *   rechargement, puisque le serveur, et non l'onglet, porte le départ.
 */

import type { CanvasDocumentDepart } from '@hector/shared-types';
import { canvasDocumentRuntime } from './canvasDocumentRuntime';
import { DEFAULT_HYDRATION_TIMEOUT_MS, type CanvasResidentRequest } from './canvasResidenceCapacity';
import { capturerLePlanDuScope } from './capturePlanDuCanvas';
import {
  presentationDeRemplacementPourUnTravail,
  remplacerLeDocumentPourUnTravail,
} from './canvasResidentPresentation';
import {
  decrireLeRefusDeSortiePourLAgent,
  ouvrirUnDocumentVierge,
  preparerLaSortieDuDocumentCourant,
  type SortieDuDocumentCourant,
} from './canvasBasculeDeBrouillon';

/** Issue d'une admission. Un refus, lui, est LEVÉ avec sa cause. */
export type IssueDAdmission =
  | {
    readonly statut: 'preparee';
    /** Le montage ADMIS — celui qui recevra le travail, donc le remplaçant s'il y en a un. */
    readonly mountEpoch: number;
    /**
     * La révision du montage admis. Sans remplacement, c'est aussi celle sous
     * laquelle le plan a été vérifié stable ; avec remplacement, le plan a été
     * vérifié sur le SOURCE et voyage par `planMarkdown` — le reçu, lui, nomme
     * le document que le worker va remplir.
     */
    readonly revision: string;
    readonly planMarkdown?: string;
    /**
     * L'épingle que l'admission a POSÉE, à rendre au terminal du flux — `null`
     * quand elle a réutilisé celle du tour, qui appartient au tour.
     */
    readonly releasePin: (() => void) | null;
  }
  | { readonly statut: 'abandonnee' };

/**
 * Prépare le résident exact d'une conversation pour un travail de rédaction.
 *
 * @throws une `Error` qui nomme la conversation, l'époque et la cause — y compris
 * `CanvasResidenceUnavailableError` (capacité) venue de `requestResident`, que
 * l'appelant traduit pour l'agent.
 */
export async function preparerLAdmissionDuResident(input: {
  readonly conversationId: string;
  readonly requestId: string;
  readonly kind: 'research' | 'generation';
  readonly capturePlanMarkdown: boolean;
  readonly seed: CanvasResidentRequest['seed'];
  /** L'épingle AUTHENTIQUE que le tour courant tient sur ce document, s'il en tient une. */
  readonly epingleDuTour: (() => void) | null;
  /** Un travail de ce fil a-t-il déjà été affiché dans ce canvas ? (`presentationDeRemplacementPourUnTravail`) */
  readonly aDejaRedigeIci: boolean;
  readonly signal: AbortSignal;
  /** Le papier à en-tête du travail (`canvas_admission_prepare.depart`) ; absent : un job antérieur. */
  readonly depart?: CanvasDocumentDepart;
  /** Enregistre le document sortant d'un document neuf ; injectable pour les tests. */
  readonly preparerLaSortie?: (conversationId: string) => Promise<SortieDuDocumentCourant>;
  /**
   * Ce qui reste de l'échéance unique du gestionnaire
   * (`CANVAS_ADMISSION_BROWSER_DEADLINE_MS`), lu à chaque attente : le montage
   * à froid reçoit tout le reste, l'attente du remplaçant au plus l'enveloppe
   * d'un remontage.
   */
  readonly budgetRestantMs: () => number;
}): Promise<IssueDAdmission> {
  const { conversationId } = input;
  await canvasDocumentRuntime.requestResident({
    conversationId,
    seed: input.seed,
    visibility: 'preserve',
    signal: input.signal,
    hydrationBudgetMs: input.budgetRestantMs,
  });
  // Pas de contrôle de propriétaire : la session est TROUVÉE par la
  // conversation, le comparer ensuite serait une tautologie (#1336). Reste ce
  // qui tranche — y a-t-il un montage, et est-il prêt ?
  const ready = canvasDocumentRuntime.getSessionForConversation(conversationId);
  if (!ready || ready.state !== 'ready') {
    throw new Error(
      `exact resident is not ready for conversationId=${conversationId} `
      + `(state=${ready?.state ?? 'aucun montage'})`,
    );
  }

  const posee = input.epingleDuTour
    ? null
    : canvasDocumentRuntime.pinSession(conversationId, { kind: 'lease', id: `admission:${input.requestId}` });
  const epingle = input.epingleDuTour ?? posee;
  const rendre = (): void => { posee?.(); };
  try {
    let revision: string | null = null;
    let planMarkdown: string | undefined;
    // Une épingle empêche l'éviction, pas une frappe. Seule une révision STABLE
    // autour de la lecture prouve que le plan capturé est celui de cette
    // révision ; une édition concurrente vaut un second essai, puis un refus.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const before = (await canvasDocumentRuntime.syncRevisionFromEditor(conversationId)) ?? ready.revision;
      if (!before) break;
      const capturedPlan = input.capturePlanMarkdown ? await capturerLePlanDuScope(conversationId) : undefined;
      const after = (await canvasDocumentRuntime.syncRevisionFromEditor(conversationId)) ?? ready.revision;
      if (before === after) {
        revision = after;
        planMarkdown = capturedPlan;
        break;
      }
    }
    if (!revision) {
      throw new Error(
        `exact resident changed while capturing admission for conversationId=${conversationId} `
        + `mountEpoch=${ready.mountEpoch}`,
      );
    }
    if (input.capturePlanMarkdown && !planMarkdown?.trim()) {
      throw new Error(
        `generation admission requires a non-empty plan snapshot for conversationId=${conversationId} `
        + `mountEpoch=${ready.mountEpoch}`,
      );
    }
    const source = canvasDocumentRuntime.getSessionForConversation(conversationId);
    if (!source || source.state !== 'ready' || source.mountEpoch !== ready.mountEpoch) {
      throw new Error(
        `resident changed while preparing admission for conversationId=${conversationId}: `
        + `admitted mountEpoch=${ready.mountEpoch}, now ${source?.mountEpoch ?? 'aucun montage'}`,
      );
    }
    if (input.signal.aborted) {
      rendre();
      return { statut: 'abandonnee' };
    }

    if (input.kind === 'research' && input.depart !== undefined) {
      const admis = await demarrerUnDocumentNeuf({ ...input, depart: input.depart, epingle, mountEpoch: source.mountEpoch });
      if (input.signal.aborted) {
        rendre();
        return { statut: 'abandonnee' };
      }
      return { statut: 'preparee', mountEpoch: admis.mountEpoch, revision: admis.revision, planMarkdown, releasePin: posee };
    }

    const remplacante = presentationDeRemplacementPourUnTravail({
      conversationId,
      kind: input.kind,
      aDejaRedigeIci: input.aDejaRedigeIci,
      depart: input.depart,
    });
    if (!remplacante) {
      return { statut: 'preparee', mountEpoch: source.mountEpoch, revision, planMarkdown, releasePin: posee };
    }

    const bail = canvasDocumentRuntime.tryBeginExclusiveReplacement(conversationId, epingle ?? undefined);
    if (!bail) {
      throw new Error(
        `the ${input.kind} admission cannot replace the document of conversationId=${conversationId} `
        + `mountEpoch=${source.mountEpoch}: another documentary job, a lawyer gesture or a second pin holds it `
        + '(the requesting turn or admission may be its only protection). Nothing was replaced and nothing '
        + 'will be enqueued; retry once that work is released.',
      );
    }
    let remplacement: ReturnType<typeof remplacerLeDocumentPourUnTravail>;
    try {
      remplacement = remplacerLeDocumentPourUnTravail({
        target: { identity: { conversationId, mountEpoch: source.mountEpoch } },
        presentation: remplacante,
        replacementLease: bail,
      });
    } finally {
      // Le bail ne couvre que le commit SYNCHRONE ; l'épingle, elle, reste
      // posée et protège le remplaçant de l'éviction pendant l'attente.
      bail.release();
    }
    if (!remplacement.accepted) {
      throw new Error(
        `the mounted editor refused the ${input.kind} replacement (reason=${remplacement.reason}) for `
        + `conversationId=${conversationId} mountEpoch=${source.mountEpoch}. Nothing will be enqueued.`,
      );
    }
    // Attendre le montage REMPLAÇANT, et lui seul (D3) : l'époque que ce
    // remplacement vient d'allouer, portant le document qu'il a demandé. Un
    // remontage survenu pendant l'attente fait refuser l'attente — ce reçu
    // désignerait sinon un document que personne n'a admis.
    const admis = await canvasDocumentRuntime.waitForReady({
      conversationId,
      mountEpoch: remplacement.session.mountEpoch,
      documentId: remplacante.templateMeta?.documentId ?? null,
    }, Math.min(DEFAULT_HYDRATION_TIMEOUT_MS, input.budgetRestantMs()));
    if (input.signal.aborted) {
      rendre();
      return { statut: 'abandonnee' };
    }
    return { statut: 'preparee', mountEpoch: admis.mountEpoch, revision: admis.revision, planMarkdown, releasePin: posee };
  } catch (error: unknown) {
    rendre();
    throw error;
  }
}

/**
 * Pose la page neuve d'une recherche qui porte un papier à en-tête, et attend SON montage.
 *
 * @throws une `Error` qui nomme la conversation et la cause ; rien n'est alors enfilé.
 */
async function demarrerUnDocumentNeuf(input: {
  readonly conversationId: string;
  readonly depart: CanvasDocumentDepart;
  readonly epingle: (() => void) | null;
  readonly mountEpoch: number;
  readonly budgetRestantMs: () => number;
  readonly preparerLaSortie?: (conversationId: string) => Promise<SortieDuDocumentCourant>;
}) {
  const { conversationId } = input;
  const issue = await ouvrirUnDocumentVierge(
    conversationId,
    input.preparerLaSortie ?? ((id) => preparerLaSortieDuDocumentCourant(id, { creeUnDocument: true })),
    input.depart,
    input.epingle ?? undefined,
  );
  switch (issue.issue) {
    case 'vierge':
      // Attendre le montage de la page neuve, et lui seul : c'est elle que le travail remplira.
      // Même enveloppe que l'attente d'un remplaçant : au plus un remontage, dans l'échéance restante.
      return canvasDocumentRuntime.waitForReady(
        { conversationId, mountEpoch: issue.mountEpoch, documentId: null },
        Math.min(DEFAULT_HYDRATION_TIMEOUT_MS, input.budgetRestantMs()),
      );
    case 'refus-de-sortie':
      throw new Error(decrireLeRefusDeSortiePourLAgent(conversationId, issue.refus));
    case 'protege':
      throw new Error(
        `the research admission cannot start a new document in conversationId=${conversationId} `
        + `mountEpoch=${input.mountEpoch}: another documentary job, a lawyer gesture or a second pin holds the `
        + 'open document. Nothing was replaced and nothing will be enqueued; retry once that work is released.',
      );
    case 'montage-remplace':
      throw new Error(
        `the research admission cannot start a new document in conversationId=${conversationId}: the open `
        + `document was remounted (was mountEpoch=${input.mountEpoch}) while it was being saved. Nothing will be `
        + 'enqueued; retry the launch.',
      );
  }
}
